import { DocumentParameters, TeslaEntry } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, Input, ViewEncapsulation, effect, inject, signal } from '@angular/core';
import { Api3Service } from '@app/services/api3.service';
import { LoadingDotsComponent } from '@app/chat/components/loading-dots/loading-dots.component';
import { TeslaRendererComponent } from '../renderer/tesla/tesla-renderer.component';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-tesla-articles',
  standalone: true,
  imports: [CommonModule, LoadingDotsComponent, TeslaRendererComponent],
  template: `
    @if (isLoading()) {
      <app-loading-dots />
    } @else {
      <app-tesla-renderer [data]="teslaDocument()" />
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class TeslaArticlesComponent {
  @Input({ required: true }) documentParameters?: DocumentParameters;

  teslaDocument = signal<TeslaEntry | undefined>(undefined);
  isLoading = signal(true);

  api3Service = inject(Api3Service);

  init = effect(async () => {
    if (!this.documentParameters) {
      console.warn('[TeslaArticlesComponent] DocumentParameters is undefined or missing.');
      this.isLoading.set(false);
      return;
    }

    // Extract guid from documentParameters
    const guid = this.documentParameters.guid;

    if (!guid) {
      console.warn('[TeslaArticlesComponent] guid is missing from documentParameters.');
      this.isLoading.set(false);
      return;
    }

    try {
      const { data, error } = await this.api3Service.api.rmi.getTeslaById({ guid });

      if (error) {
        console.error('[TeslaArticlesComponent] Getting Tesla document failed', error);

        Sentry.captureException(error, {
          tags: {
            location: 'TeslaArticlesComponent',
            feature: 'ArticleView',
            message: 'Failed to load Tesla article.',
          },
        });
      }

      this.teslaDocument.set(data);
    } catch (error) {
      console.error('[TeslaArticlesComponent] Exception while fetching Tesla document', error);
      Sentry.captureException(error, {
        tags: {
          location: 'TeslaArticlesComponent',
          feature: 'ArticleView',
          message: 'Exception while fetching Tesla document.',
        },
      });
    }

    this.isLoading.set(false);
  });
}
